import { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import api from '../../services/api';
import toast from 'react-hot-toast';
import {
  CloudArrowUpIcon,
  DocumentTextIcon,
  TableCellsIcon,
  CheckCircleIcon,
  ArrowPathIcon,
  DocumentDuplicateIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import RawDataGrid from './RawDataGrid';
import DataRangeSelector from './DataRangeSelector';
import { MAX_DATA_FILE_SIZE, MAX_DATA_FILE_SIZE_LABEL } from '../../constants/uploadLimits';
import { COLUMN_TYPES } from '../../constants/columnTypes';
import { uploadDataSourceFile, shouldUseDirectUpload } from '../../utils/dataFileUpload';

export default function FileUploadSource({ type, companyId, onSuccess, onBack }) {
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [fileBuffer, setFileBuffer] = useState(null);
  const [gcsUploadId, setGcsUploadId] = useState(null);
  const [preview, setPreview] = useState(null);
  const [name, setName] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [sheets, setSheets] = useState([]);
  const [selectedSheet, setSelectedSheet] = useState(null);
  const [selectedColumns, setSelectedColumns] = useState(new Set());
  const [columnTypes, setColumnTypes] = useState({});
  const [columnSearchTerm, setColumnSearchTerm] = useState('');

  // Range selection state
  const [rawPreview, setRawPreview] = useState(null);
  const [headerRow, setHeaderRow] = useState(0);
  const [dataRange, setDataRange] = useState({ startRow: 0, endRow: undefined, startCol: 0, endCol: undefined });
  const [isApplyingRange, setIsApplyingRange] = useState(false);

  const initColumnTypes = (columns, previousTypes = {}) => {
    const types = {};
    columns?.forEach(col => {
      types[col.name] = previousTypes[col.name] || col.type || 'STRING';
    });
    setColumnTypes(types);
  };

  const initRangeFromResponse = (data) => {
    if (data.rawPreview) {
      setRawPreview(data.rawPreview);
      const rp = data.rawPreview;
      const detected = rp.detectedHeaderRow || 0;
      setHeaderRow(detected);
      setDataRange({
        startRow: detected + 1,
        endRow: undefined,
        startCol: rp.detectedStartColumn || 0,
        endCol: rp.detectedEndColumn != null ? rp.detectedEndColumn : undefined
      });
    }
  };
  
  const onDrop = useCallback(async (acceptedFiles, rejectedFiles) => {
    if (rejectedFiles?.length > 0) {
      const err = rejectedFiles[0].errors[0];
      if (err?.code === 'file-too-large') {
        toast.error(`File is too large. Maximum size is ${MAX_DATA_FILE_SIZE_LABEL}.`);
      } else if (err?.code === 'file-invalid-type') {
        toast.error(type === 'csv' ? 'Invalid file type. Please upload a CSV file.' : 'Invalid file type. Please upload an XLS or XLSX file.');
      } else {
        toast.error(err?.message || 'File could not be accepted');
      }
      return;
    }

    const uploadedFile = acceptedFiles[0];
    if (!uploadedFile) return;
    
    if (!companyId) {
      toast.error('Please select a company first');
      return;
    }
    
    setFile(uploadedFile);
    setFileBuffer(shouldUseDirectUpload(uploadedFile) ? null : uploadedFile);
    setGcsUploadId(null);
    setName(uploadedFile.name.replace(/\.[^/.]+$/, ''));
    setIsUploading(true);
    
    try {
      const response = await uploadDataSourceFile(uploadedFile, {
        companyId,
        name: uploadedFile.name
      });
      
      if (response.data.requiresSheetSelection) {
        setSheets(response.data.sheets);
        if (response.data.uploadId) {
          setGcsUploadId(response.data.uploadId);
        }
        const firstWithData = response.data.sheets.find(s => s.hasData) || response.data.sheets[0];
        setSelectedSheet(firstWithData?.name);
        setStep(2);
      } else {
        setPreview(response.data);
        initRangeFromResponse(response.data);
        const allColumns = response.data.schema?.columns?.map(c => c.name) || [];
        setSelectedColumns(new Set(allColumns));
        initColumnTypes(response.data.schema?.columns);
        setStep(3);
      }
    } catch (error) {
      console.error('Upload error:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to upload file';
      const errorCode = error.response?.data?.code;
      
      if (errorCode === 'COMPANY_REQUIRED') {
        toast.error('Please select a company before uploading files');
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setIsUploading(false);
    }
  }, [companyId, type]);
  
  const handleSheetSelection = async () => {
    if (!selectedSheet || (!fileBuffer && !gcsUploadId)) return;
    
    if (!companyId) {
      toast.error('Please select a company first');
      return;
    }
    
    setIsUploading(true);
    
    try {
      let response;

      if (gcsUploadId) {
        response = await api.post('/data-sources/upload/complete', {
          uploadId: gcsUploadId,
          name: name || file?.name,
          companyId,
          sheetName: selectedSheet
        }, {
          timeout: 10 * 60 * 1000
        });
      } else {
        response = await uploadDataSourceFile(fileBuffer, {
          companyId,
          name: name || fileBuffer.name,
          sheetName: selectedSheet
        });
      }
      
      setPreview(response.data);
      initRangeFromResponse(response.data);
      const allColumns = response.data.schema?.columns?.map(c => c.name) || [];
      setSelectedColumns(new Set(allColumns));
      initColumnTypes(response.data.schema?.columns);
      setStep(3);
    } catch (error) {
      console.error('Sheet selection upload error:', error);
      const errorMessage = error.response?.data?.error || 'Failed to process selected sheet';
      const errorCode = error.response?.data?.code;
      
      if (errorCode === 'COMPANY_REQUIRED') {
        toast.error('Please select a company before uploading files');
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setIsUploading(false);
    }
  };

  const handleApplyRange = async () => {
    if (!preview?.dataSource?._id) return;
    setIsApplyingRange(true);

    try {
      const response = await api.post(`/data-sources/${preview.dataSource._id}/reparse`, {
        headerRow,
        startRow: dataRange.startRow,
        endRow: dataRange.endRow,
        startColumn: dataRange.startCol ?? undefined,
        endColumn: dataRange.endCol ?? undefined
      });

      setPreview(prev => ({
        ...prev,
        preview: response.data.preview,
        schema: response.data.schema
      }));
      if (response.data.rawPreview) {
        setRawPreview(response.data.rawPreview);
      }
      const allColumns = response.data.schema?.columns?.map(c => c.name) || [];
      setSelectedColumns(new Set(allColumns));
      initColumnTypes(response.data.schema?.columns, columnTypes);
      toast.success('Data range updated');
    } catch (error) {
      console.error('Reparse error:', error);
      toast.error(error.response?.data?.error || 'Failed to apply range');
    } finally {
      setIsApplyingRange(false);
    }
  };
  
  const acceptedTypes = type === 'csv' 
    ? { 'text/csv': ['.csv'] }
    : { 
        'application/vnd.ms-excel': ['.xls'],
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx']
      };
  
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: acceptedTypes,
    maxFiles: 1,
    maxSize: MAX_DATA_FILE_SIZE
  });
  
  const handleColumnTypeChange = (columnName, type) => {
    setColumnTypes(prev => ({ ...prev, [columnName]: type }));
  };

  const handleImport = async () => {
    if (!preview?.dataSource?._id) {
      toast.error('No data source created');
      return;
    }
    
    if (selectedColumns.size === 0) {
      toast.error('Please select at least one column to import');
      return;
    }
    
    setIsImporting(true);
    setStep(4);
    
    try {
      await api.post(`/data-sources/${preview.dataSource._id}/import`, { 
        mode: 'full',
        columns: Array.from(selectedColumns),
        columnTypes: Object.fromEntries(
          Array.from(selectedColumns).map(colName => [colName, columnTypes[colName] || 'STRING'])
        ),
        name: name.trim() || preview.dataSource.name,
        dataRange: {
          headerRow,
          startRow: dataRange.startRow,
          endRow: dataRange.endRow,
          startColumn: dataRange.startCol,
          endColumn: dataRange.endCol
        }
      });
      toast.success('Import started!');
      onSuccess({ ...preview.dataSource, name: name.trim() || preview.dataSource.name });
    } catch (error) {
      console.error('Import error:', error);
      toast.error('Failed to start import');
      setStep(3);
    } finally {
      setIsImporting(false);
    }
  };
  
  const handleColumnToggle = (columnName) => {
    setSelectedColumns(prev => {
      const newSet = new Set(prev);
      if (newSet.has(columnName)) {
        newSet.delete(columnName);
      } else {
        newSet.add(columnName);
      }
      return newSet;
    });
  };
  
  const handleSelectAllColumns = () => {
    const allColumns = preview?.schema?.columns?.map(c => c.name) || [];
    if (selectedColumns.size === allColumns.length) {
      setSelectedColumns(new Set());
    } else {
      setSelectedColumns(new Set(allColumns));
    }
  };
  
  const Icon = type === 'csv' ? DocumentTextIcon : TableCellsIcon;
  
  const hasSheetSelection = type === 'excel' && sheets.length > 1;
  const getSteps = () => {
    if (hasSheetSelection) {
      return [
        { num: 1, label: 'Upload' },
        { num: 2, label: 'Select Sheet' },
        { num: 3, label: 'Preview' },
        { num: 4, label: 'Import' }
      ];
    }
    return [
      { num: 1, label: 'Upload', displayNum: 1 },
      { num: 3, label: 'Preview', displayNum: 2 },
      { num: 4, label: 'Import', displayNum: 3 }
    ];
  };
  
  const steps = getSteps();
  
  return (
    <div className="space-y-6">
      {/* Progress indicator */}
      <div className="flex items-center gap-4">
        {steps.map((s, i) => (
          <div key={s.num} className="flex items-center gap-3">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-medium text-sm ${
              step >= s.num 
                ? 'bg-primary-600 text-white' 
                : 'bg-surface-200 dark:bg-surface-700 text-surface-500'
            }`}>
              {step > s.num ? <CheckCircleIcon className="w-5 h-5" /> : (s.displayNum || s.num)}
            </div>
            <span className={`text-sm ${step >= s.num ? 'text-surface-900 dark:text-white' : 'text-surface-500'}`}>
              {s.label}
            </span>
            {i < steps.length - 1 && <div className="w-12 h-px bg-surface-200 dark:bg-surface-700" />}
          </div>
        ))}
      </div>
      
      {/* Step 1: Upload */}
      {step === 1 && (
        <div className="card p-8">
          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-colors ${
              isDragActive 
                ? 'border-primary-500 bg-primary-50 dark:bg-primary-950' 
                : 'border-surface-300 dark:border-surface-700 hover:border-primary-400'
            }`}
          >
            <input {...getInputProps()} />
            
            {isUploading ? (
              <div>
                <ArrowPathIcon className="w-12 h-12 mx-auto mb-4 text-primary-500 animate-spin" />
                <p className="text-surface-600 dark:text-surface-400">
                  {file && shouldUseDirectUpload(file)
                    ? 'Uploading to cloud storage and analyzing...'
                    : 'Uploading and analyzing...'}
                </p>
              </div>
            ) : (
              <>
                <CloudArrowUpIcon className="w-12 h-12 mx-auto mb-4 text-surface-400" />
                <p className="text-lg font-medium text-surface-900 dark:text-white mb-2">
                  {isDragActive ? 'Drop your file here' : 'Drag & drop a file here'}
                </p>
                <p className="text-surface-500 dark:text-surface-400 mb-4">
                  or click to browse
                </p>
                <p className="text-sm text-surface-400">
                  {type === 'csv' ? 'Supports CSV files' : 'Supports XLS, XLSX files'} • Max {MAX_DATA_FILE_SIZE_LABEL}
                </p>
              </>
            )}
          </div>
          
          <div className="flex justify-between mt-6">
            <button onClick={onBack} className="btn-secondary">
              Back
            </button>
          </div>
        </div>
      )}
      
      {/* Step 2: Sheet Selection (Excel only) */}
      {step === 2 && sheets.length > 0 && (
        <div className="card p-6">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-12 rounded-xl bg-primary-100 dark:bg-primary-900 flex items-center justify-center">
              <DocumentDuplicateIcon className="w-6 h-6 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h3 className="text-lg font-medium text-surface-900 dark:text-white">
                Select a Sheet to Import
              </h3>
              <p className="text-sm text-surface-500">
                This Excel file contains {sheets.length} sheets. Choose which one to import.
              </p>
            </div>
          </div>
          
          <div className="space-y-2 mb-6">
            {sheets.map((sheet) => (
              <label
                key={sheet.name}
                className={`flex items-center justify-between p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  selectedSheet === sheet.name
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-950'
                    : 'border-surface-200 dark:border-surface-700 hover:border-primary-300'
                }`}
              >
                <div className="flex items-center gap-3">
                  <input
                    type="radio"
                    name="sheet"
                    value={sheet.name}
                    checked={selectedSheet === sheet.name}
                    onChange={(e) => setSelectedSheet(e.target.value)}
                    className="w-4 h-4 text-primary-600 border-surface-300 focus:ring-primary-500"
                  />
                  <div>
                    <span className="font-medium text-surface-900 dark:text-white">
                      {sheet.name}
                    </span>
                    {!sheet.hasData && (
                      <span className="ml-2 text-xs text-amber-600 dark:text-amber-400">
                        (empty)
                      </span>
                    )}
                  </div>
                </div>
                <span className="text-sm text-surface-500">
                  {sheet.rowCount.toLocaleString()} rows
                </span>
              </label>
            ))}
          </div>
          
          <div className="flex items-center justify-between">
            <button 
              onClick={() => {
                setStep(1);
                setSheets([]);
                setSelectedSheet(null);
                setGcsUploadId(null);
              }} 
              className="btn-secondary"
            >
              Back
            </button>
            <button 
              onClick={handleSheetSelection} 
              disabled={!selectedSheet || isUploading}
              className="btn-primary"
            >
              {isUploading ? (
                <>
                  <ArrowPathIcon className="w-5 h-5 animate-spin mr-2" />
                  Processing...
                </>
              ) : (
                'Continue with Selected Sheet'
              )}
            </button>
          </div>
        </div>
      )}
      
      {/* Step 3: Preview */}
      {step === 3 && preview && (
        <div className="space-y-6">
          {/* Data Source Name */}
          <div className="card p-4">
            <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
              Data Source Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="input"
              placeholder={file?.name?.replace(/\.[^/.]+$/, '') || 'Enter a name for this data source'}
            />
          </div>
          
          {/* File Info Summary */}
          <div className="card p-4">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-primary-100 dark:bg-primary-900 flex items-center justify-center">
                <Icon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
              </div>
              <div>
                <p className="font-medium text-surface-900 dark:text-white">{file?.name}</p>
                <p className="text-sm text-surface-500">
                  {preview.schema?.columns?.length} columns • {preview.schema?.totalRows?.toLocaleString()} rows
                  {preview.sheetInfo?.selectedSheet && (
                    <span className="ml-2 text-primary-600 dark:text-primary-400">
                      • Sheet: {preview.sheetInfo.selectedSheet}
                    </span>
                  )}
                </p>
              </div>
            </div>
          </div>

          {/* Raw Data Grid + Range Selector */}
          {rawPreview && rawPreview.rows?.length > 0 && (
            <>
              <DataRangeSelector
                detectedHeaderRow={rawPreview.detectedHeaderRow || 0}
                totalRows={rawPreview.totalRawRows || rawPreview.rows.length}
                totalColumns={rawPreview.totalRawColumns || 0}
                headerRow={headerRow}
                dataRange={dataRange}
                onHeaderRowChange={setHeaderRow}
                onDataRangeChange={setDataRange}
                onApply={handleApplyRange}
                isApplying={isApplyingRange}
                csvInfo={type === 'csv' ? {
                  detectedDelimiter: rawPreview.detectedDelimiter,
                  detectedEncoding: rawPreview.detectedEncoding
                } : null}
              />
              <div className="card p-4">
                <h3 className="font-medium text-surface-900 dark:text-white mb-3 text-sm">Raw File Preview</h3>
                <RawDataGrid
                  rows={rawPreview.rows}
                  headerRow={headerRow}
                  dataRange={dataRange}
                />
              </div>
            </>
          )}
          
          {/* Column Selection */}
          <div className="card p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-medium text-surface-900 dark:text-white">
                Columns & Types ({selectedColumns.size} of {preview.schema?.columns?.length || 0})
              </h3>
              <button
                onClick={handleSelectAllColumns}
                className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
              >
                {selectedColumns.size === (preview.schema?.columns?.length || 0) ? 'Deselect All' : 'Select All'}
              </button>
            </div>
            <p className="text-xs text-surface-500 mb-3">
              Adjust column types if auto-detection is wrong (e.g. set a date-like text column to STRING).
            </p>
            
            {(preview.schema?.columns?.length || 0) > 8 && (
              <div className="relative mb-3">
                <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-surface-400" />
                <input
                  type="text"
                  value={columnSearchTerm}
                  onChange={(e) => setColumnSearchTerm(e.target.value)}
                  placeholder="Search columns..."
                  className="input pl-9 py-2 text-sm w-full"
                />
                {columnSearchTerm && (
                  <button
                    onClick={() => setColumnSearchTerm('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-surface-400 hover:text-surface-600"
                  >
                    ×
                  </button>
                )}
              </div>
            )}
            
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {preview.schema?.columns
                ?.filter(col => col.name.toLowerCase().includes(columnSearchTerm.toLowerCase()))
                .map((col, i) => (
                  <div
                    key={i}
                    className={`flex items-center gap-2 p-2 rounded-lg transition-colors ${
                      selectedColumns.has(col.name)
                        ? 'bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800'
                        : 'bg-surface-50 dark:bg-surface-800 border border-transparent'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedColumns.has(col.name)}
                      onChange={() => handleColumnToggle(col.name)}
                      className="rounded text-primary-600"
                    />
                    <p
                      className="text-sm font-medium text-surface-900 dark:text-white truncate flex-1 min-w-0"
                      title={col.name}
                    >
                      {col.name}
                    </p>
                    <select
                      value={columnTypes[col.name] || col.type || 'STRING'}
                      onChange={(e) => handleColumnTypeChange(col.name, e.target.value)}
                      disabled={!selectedColumns.has(col.name)}
                      className="input py-1 px-2 text-xs w-32 shrink-0 disabled:opacity-50"
                    >
                      {COLUMN_TYPES.map(t => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>
                ))}
              {preview.schema?.columns?.filter(col => col.name.toLowerCase().includes(columnSearchTerm.toLowerCase())).length === 0 && (
                <div className="text-center py-4 text-surface-500 text-sm">
                  No columns match "{columnSearchTerm}"
                </div>
              )}
            </div>
          </div>
          
          {/* Data Preview */}
          <div className="card p-4">
            <h3 className="font-medium text-surface-900 dark:text-white mb-3">Data Preview</h3>
            {selectedColumns.size === 0 ? (
              <div className="text-center py-8 text-surface-500 border border-surface-200 dark:border-surface-700 rounded-lg">
                Please select at least one column to preview
              </div>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-surface-200 dark:border-surface-700">
                <table className="w-full text-sm">
                  <thead className="bg-surface-50 dark:bg-surface-800">
                    <tr>
                      {preview.schema?.columns?.filter(col => selectedColumns.has(col.name)).map((col, i) => (
                        <th key={i} className="px-4 py-2 text-left font-medium text-surface-600 dark:text-surface-400 whitespace-nowrap">
                          {col.name}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.preview?.slice(0, 10).map((row, i) => (
                      <tr key={i} className="border-t border-surface-100 dark:border-surface-800">
                        {preview.schema?.columns?.filter(col => selectedColumns.has(col.name)).map((col, j) => (
                          <td key={j} className="px-4 py-2 text-surface-700 dark:text-surface-300 whitespace-nowrap max-w-xs truncate">
                            {String(row[col.name] ?? '')}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          
          <div className="flex items-center justify-between">
            <button 
              onClick={() => {
                if (sheets.length > 1) {
                  setStep(2);
                } else {
                  setStep(1);
                }
              }} 
              className="btn-secondary"
            >
              Back
            </button>
            <button onClick={handleImport} disabled={isImporting || selectedColumns.size === 0} className="btn-primary">
              {isImporting && <ArrowPathIcon className="w-5 h-5 animate-spin mr-2" />}
              Import {selectedColumns.size > 0 ? `${selectedColumns.size} Columns` : 'Data'}
            </button>
          </div>
        </div>
      )}
      
      {/* Step 4: Importing */}
      {step === 4 && (
        <div className="card p-12 text-center">
          <ArrowPathIcon className="w-12 h-12 mx-auto mb-4 text-primary-500 animate-spin" />
          <h3 className="text-lg font-medium text-surface-900 dark:text-white mb-2">
            Importing Data...
          </h3>
          <p className="text-surface-500 dark:text-surface-400">
            Your data is being imported to BigQuery. This may take a few moments.
          </p>
        </div>
      )}
    </div>
  );
}
